import { formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Users, Calendar, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';
import { Notification } from '@/services/notificationService';

const formatTimeAgo = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    if (!isValid(date)) return '';
    
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: enUS
    });
  } catch {
    return '';
  }
};

interface NotificationItemProps {
  notification: Notification;
}

export const NotificationItem = ({ notification }: NotificationItemProps) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotifications();

  const getIcon = () => {
    switch (notification.type) {
      case 'message':
        return <MessageCircle className="h-4 w-4 text-blue-500" />;
      case 'nudge':
        return <Users className="h-4 w-4 text-green-500" />;
      case 'checkin_reminder':
        return <Calendar className="h-4 w-4 text-orange-500" />;
      default:
        return <Bell className="h-4 w-4 text-gray-500" />;
    }
  };

  const handleClick = async () => {
    console.log('🔔 Notification clicked:', {
      id: notification.id,
      type: notification.type,
      title: notification.title,
      action_url: notification.action_url,
      read: notification.read
    });

    if (!notification.read) {
      console.log('📖 Marking notification as read...');
      await markAsRead(notification.id);
    }

    // Handle navigation based on notification type and action_url
    if (notification.type === 'checkin_reminder' || notification.title.toLowerCase().includes('check-in')) {
      console.log('🎯 Navigating to check-in page via type/title match');
      navigate('/check-in');
      return;
    }
    
    if (notification.action_url) {
      // Ensure the URL starts with '/' for internal navigation
      const url = notification.action_url.startsWith('/') ? notification.action_url : `/${notification.action_url}`;
      console.log('🎯 Navigating to action_url:', url);
      navigate(url);
      return;
    }

    // Fallback: specific title handling for legacy notifications
    if (notification.title === 'Team Check-in Request') {
      console.log('🎯 Navigating to check-in page via legacy title match');
      navigate('/check-in');
      return;
    }
    if (notification.title === 'Team Celebration') {
      console.log('🎉 Team celebration notification - no navigation');
      return;
    }

    console.log('⚠️ No navigation action for this notification');
  };

  const timeAgo = formatTimeAgo(notification.created_at);

  return (
    <div
      className={cn(
        "p-4 cursor-pointer hover:bg-muted/50 transition-colors",
        !notification.read && "bg-blue-50/50"
      )}
      onClick={handleClick}
    >
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 mt-1">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className={cn(
              "text-sm font-medium truncate",
              !notification.read && "font-semibold"
            )}>
              {notification.title}
            </p>
            {!notification.read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2" />
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {notification.message}
          </p>
          {timeAgo && (
            <p className="text-xs text-muted-foreground mt-2">
              {timeAgo}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
